/**
 * Anti-VIX readings for the Market Overview panel.
 *
 * **LIVE** — QuantForge `GET /v1/steamroom/vix-strength` (their PR #985,
 * `de66967d`, deployed 2026-08-21). Background and the measurements that led to
 * this endpoint: `app/docs/plans/2026-08-21-anti-vix-qf-bars-handoff.md`.
 *
 * ⚠️ **The server computes the indicator; we only plot it.** This reverses the
 * split we originally built for. QF's reasoning, and it is right: a formula in a
 * frontend bundle is readable and reverse-engineerable, which defeats the point
 * of gating the route. There is now ONE implementation of the maths and it is
 * theirs.
 *
 * ⚠️ **There is no local port any more.** `utils/vixStrength.ts` and its tests
 * were deleted on cutover (2026-08-21). Keeping a second implementation would
 * re-create exactly the drift risk that argued for a single source of truth —
 * two ports, and no test spanning them. If the maths ever needs checking, check
 * it against the Pine script and QF's `services/vix_strength.py`, not against a
 * copy in this repo.
 *
 * ⚠️ **`time` is the bucket OPEN, not its close.** It matches `/v1/bars` and the
 * `ohlc:{SYM}:1m` stream. Pine stamps bars at their close, so reading these as
 * close-stamps reports the panel 65 minutes fresher than it is — see
 * `closeTimeOf`, which every freshness label must go through. QF chose open
 * because it is the unambiguous end: a bar's close is the next bar's open, so a
 * close-stamped final 14:55 bar would read 16:00, a time at which no bar exists.
 *
 * ⚠️ **Namespaced, and therefore gated.** In QF's convention the `steamroom`
 * URL segment marks a route as permission-gated: admission is checked against
 * Steamroom Django per user and **fails closed** — a cache miss during a Django
 * outage is a 403, not stale data. Anti-VIX sits on Market Overview beside
 * ungated cards, so a gate narrower than the page shows one broken card on an
 * otherwise working page. QF declined to build an ungated twin without David's
 * sign-off, correctly: widening a security boundary is not a side effect.
 */

import { qfRestGet } from "./qfClient";

/**
 * ⚠️ The `steamroom` segment is what marks this gated — see the header. There
 * is no ungated twin, and asking for one means asking QF to widen a deliberate
 * boundary.
 */
export const VIX_STRENGTH_PATH = "/v1/steamroom/vix-strength";

/**
 * Calendar days of 1-minute VIX tape the server buckets.
 *
 * ⚠️ **Days, not sessions.** QF's parameter is `lookback_days` (max 365) and an
 * unknown query param would be ignored in silence, leaving the 120-day default
 * — which happens to be right, so a wrong name here fails invisibly rather than
 * loudly. 120 days covers the 242 bars a full plot needs with room to spare,
 * and is bounded above by the 90-day TTL on `qf.spot_bars_1m` regardless.
 */
const LOOKBACK_DAYS = 120;

/**
 * Series state, as the server reports it.
 *
 * ⚠️ **`partial` is a SUCCESS state, not a soft error.** Its values are exactly
 * as correct as `ok`'s — there are simply fewer of them, because VIX history
 * began 2026-07-13 under a 90-day TTL. We are on `partial` until roughly
 * mid-September 2026. Rendering it as an error blanks a working chart for three
 * weeks.
 */
export type VixStrengthStatus = "warming_up" | "partial" | "ok";

/** One plotted reading. */
export interface VixStrengthPoint {
  /** Epoch seconds at the bar's OPEN — see `closeTimeOf` before labelling. */
  time: number;
  /** The Anti-VIX reading, 0–100, already clamped server-side. */
  value: number;
}

/** The buy/sell thresholds, served alongside the data. */
export interface VixStrengthBands {
  /**
   * ⚠️ The BUY level, despite being the lower number. The series is inverted —
   * a high VIX prints a low reading — so the bottom band is fear extended and
   * the top is complacency. Colouring this "high is good" is wrong in the
   * direction that loses money.
   */
  lower: number;
  /** The SELL level. See `lower`. */
  upper: number;
}

/** The endpoint's full payload. */
export interface VixStrengthSeries {
  symbol: string;
  /** Bar size in minutes (65). Needed to turn an open-stamp into a close. */
  intervalMinutes: number;
  status: VixStrengthStatus;
  /** Bars the server had. Below `barsRequired` the plot is short but correct. */
  barsAvailable: number;
  /** Bars a full-width plot needs (242 = 50 warm-up + 192 visible). */
  barsRequired: number;
  /**
   * ⚠️ Served, not hardcoded, so the panel's guide lines cannot drift from the
   * thresholds the server actually applied.
   */
  bands: VixStrengthBands;
  /** Readings, oldest first. Empty while `warming_up`. */
  points: VixStrengthPoint[];
}

/** Raw wire shape. Named to match QF's payload, not our domain. */
interface VixStrengthWire {
  symbol?: unknown;
  interval_minutes?: unknown;
  status?: unknown;
  bars_available?: unknown;
  bars_required?: unknown;
  bands?: { lower?: unknown; upper?: unknown };
  points?: Array<{ time?: unknown; value?: unknown }>;
}

const VALID_STATUS: readonly string[] = ["warming_up", "partial", "ok"];

/**
 * The close instant of a point, in epoch seconds.
 *
 * ⚠️ **Every freshness label must go through this.** `point.time` is the bar's
 * OPEN, so stamping the panel with it claims the reading is a full bar — 65
 * minutes — fresher than it is. The card's `Updated` line is the reason this
 * function exists.
 */
export function closeTimeOf(
  point: VixStrengthPoint,
  intervalMinutes: number,
): number {
  return point.time + intervalMinutes * 60;
}

/** A point is plottable only if both fields are real numbers. */
function isUsablePoint(p: { time?: unknown; value?: unknown }): boolean {
  return Number.isFinite(p?.time) && Number.isFinite(p?.value);
}

/**
 * Coerce the wire payload into the domain shape.
 *
 * ⚠️ **Points are sorted and de-duplicated defensively.** The chart draws them
 * in array order, so an out-of-order payload does not error — it draws a series
 * that folds back on itself while still looking like a plausible curve. On
 * duplicate timestamps the LAST wins, so a revised bar supersedes the original.
 *
 * ⚠️ **An unrecognised `status` degrades to `partial`, never to `ok`.** If QF
 * adds a state we do not know, the safe reading is "usable but possibly
 * incomplete"; calling it `ok` would assert a full window we cannot vouch for.
 *
 * Exported for its tests — this is the whole trust boundary with the endpoint.
 */
export function parseVixStrength(body: VixStrengthWire | null | undefined): VixStrengthSeries {
  const rawStatus = typeof body?.status === "string" ? body.status : "";
  const status = (VALID_STATUS.includes(rawStatus) ? rawStatus : "partial") as VixStrengthStatus;

  const byTime = new Map<number, VixStrengthPoint>();
  for (const p of body?.points ?? []) {
    if (!isUsablePoint(p)) continue;
    byTime.set(p.time as number, { time: p.time as number, value: p.value as number });
  }
  const points = [...byTime.values()].sort((a, b) => a.time - b.time);

  const num = (v: unknown, fallback: number): number =>
    Number.isFinite(v) ? (v as number) : fallback;

  return {
    symbol: typeof body?.symbol === "string" ? body.symbol : "VIX",
    // 65 is the indicator's defining constant, not a tunable default — a
    // missing value here would silently mis-stamp every freshness label.
    intervalMinutes: num(body?.interval_minutes, 65),
    status,
    barsAvailable: num(body?.bars_available, points.length),
    barsRequired: num(body?.bars_required, 242),
    bands: {
      lower: num(body?.bands?.lower, 30),
      upper: num(body?.bands?.upper, 60),
    },
    points,
  };
}

/**
 * Fetch the Anti-VIX series.
 *
 * Throws on transport or entitlement failure rather than returning an empty
 * series: an empty chart and an unreachable feed look identical in the pane,
 * and only one of them means there is nothing to show. Callers should render
 * `warming_up` (no data yet) and `partial` (short but correct) as distinct from
 * a thrown error.
 */
export async function fetchVixStrength(): Promise<VixStrengthSeries> {
  const body = await qfRestGet<VixStrengthWire>(VIX_STRENGTH_PATH, {
    lookback_days: String(LOOKBACK_DAYS),
  });
  return parseVixStrength(body);
}
